// src/components/cognition/cognition-task-understanding-table.tsx
import React from "react";

function normKey(s: string) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function CognitionTaskUnderstandingTable({
  rows,
  modelIds,
  modelLabel,
}: {
  rows: any[];
  modelIds: string[];
  modelLabel: (id: string) => string;
}) {
  const list = Array.isArray(rows) ? rows : [];

  const findRow = (id: string) => {
    const target = normKey(id);
    const label = normKey(modelLabel(id));
    for (const r of list) {
      const mk = normKey(r?.model || "");
      if (!mk) continue;
      if (mk === target || mk === label) return r;
      if (mk.includes(target) || mk.includes(label)) return r;
    }
    return {};
  };

  const confidenceDot = (c: string) => {
    const v = String(c || "").toLowerCase();
    const color =
      v === "high"
        ? "var(--alpha-teal)"
        : v === "moderate" || v === "medium"
        ? "rgba(245, 158, 11, 1)"
        : v === "low"
        ? "var(--infrared-coral)"
        : "var(--muted-foreground)";
    return <div className="w-4 h-4 rounded-full" style={{ backgroundColor: color }} />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th
              className="px-4 py-3 text-left"
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                borderTop: "1px solid var(--border)",
                borderLeft: "1px solid var(--border)",
                borderTopLeftRadius: "var(--radius)",
              }}
            />
            {modelIds.map((id) => (
              <th
                key={id}
                className="px-4 py-3 text-center"
                style={{
                  backgroundColor: "var(--alpha-teal-10)",
                  borderTop: "1px solid var(--border)",
                  borderLeft: "1px solid var(--border)",
                  fontWeight: "var(--font-weight-semibold)",
                }}
              >
                {modelLabel(id)}
              </th>
            ))}
            <th
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                borderTop: "1px solid var(--border)",
                borderLeft: "1px solid var(--border)",
                borderRight: "1px solid var(--border)",
                borderTopRightRadius: "var(--radius)",
              }}
            />
          </tr>
        </thead>

        <tbody>
          {[
            { label: "Task Type", key: "taskType" },
            { label: "Persona", key: "persona" },
            { label: "Category", key: "category" },
          ].map((rowDef) => (
            <tr key={rowDef.key} className="table-row-hover">
              <td
                className="px-4 py-3"
                style={{
                  borderLeft: "1px solid var(--border)",
                  borderTop: "1px solid var(--border)",
                  fontWeight: "var(--font-weight-semibold)",
                }}
              >
                {rowDef.label}
              </td>

              {modelIds.map((id) => (
                <td
                  key={id}
                  className="px-4 py-3 text-center"
                  style={{
                    borderLeft: "1px solid var(--border)",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  {String(findRow(id)?.[rowDef.key] ?? "—")}
                </td>
              ))}

              <td style={{ borderRight: "1px solid var(--border)", borderTop: "1px solid var(--border)" }} />
            </tr>
          ))}

          <tr className="table-row-hover">
            <td
              className="px-4 py-3"
              style={{
                borderLeft: "1px solid var(--border)",
                borderTop: "1px solid var(--border)",
                borderBottom: "1px solid var(--border)",
                borderBottomLeftRadius: "var(--radius)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              Confidence
            </td>

            {modelIds.map((id) => (
              <td
                key={id}
                className="px-4 py-3 text-center"
                style={{
                  borderLeft: "1px solid var(--border)",
                  borderTop: "1px solid var(--border)",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <div className="flex justify-center">{confidenceDot(findRow(id)?.confidence)}</div>
              </td>
            ))}

            <td
              style={{
                borderRight: "1px solid var(--border)",
                borderTop: "1px solid var(--border)",
                borderBottom: "1px solid var(--border)",
                borderBottomRightRadius: "var(--radius)",
              }}
            />
          </tr>
        </tbody>
      </table>
    </div>
  );
}
